import React from 'react'
import * as R from 'ramda'
import { createPortal } from 'react-dom'
import { throttle } from 'lodash'
import {
  usePortalContainer,
  placeTetheredChild,
  isElementVisible,
} from './helpers.js'
import cx from 'classnames'

// Event listener callback to stop event bubbling
const stopBubble = ev => {
  ev.preventDefault()
  ev.stopPropagation()
}

// Arrow that points in the direction of the tether anchor dom node
const TetherArrow = ({ style = {} }) => {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 2 14 12"
      preserveAspectRatio="xMidYMin meet"
      className="TetherArrow"
      style={style}
    >
      <path d={'M0 14L5 6Q 7 3 9 6L14 14'} />
    </svg>
  )
}

const getZindex = element => {
  if (!(element instanceof Element)) return 0
  const z = getComputedStyle(element).getPropertyValue('z-index')
  return isNaN(z) ? getZindex(element.parentNode) : parseInt(z)
}

// For tool tips or dropdown menus. Display children in tethered react portal
const TetherPortal = ({
  stage = 'open', // open | opening | closing | closed
  order = 'top bottom', // top | bottom | left | right
  throttleTimeout = 100,
  speed = 200,
  dismiss, // callback function
  anchorRef,
  children,
  className,
}) => {
  const [position, setPosition] = React.useState({})
  const [visible, setVisible] = React.useState(false)
  const [zIndex, setZindex] = React.useState(0)
  const childRef = React.useRef()
  const portalContainer = usePortalContainer()

  // move child element towards anchor element
  const centerOnAnchor = React.useCallback(() => {
    const shift = -0.25 // 25% percent of child dimension towards anchor
    const anchor = anchorRef.current
    const child = childRef.current
    if (anchor && child)
      setPosition(placeTetheredChild(anchor, child, order, shift))
  }, [anchorRef, order])

  // update tether position
  const updatePosition = React.useCallback(() => {
    if (!children) return
    const anchor = anchorRef.current
    const child = childRef.current
    if (child && isElementVisible(anchor)) {
      const newPosition = placeTetheredChild(anchor, child, order)
      // use shallow equality check to avoid re-render if position is equal
      setPosition(R.unless(R.equals(newPosition), R.always(newPosition)))
    }
  }, [children, anchorRef, order])

  React.useEffect(
    function addListeners() {
      const reposition = throttle(updatePosition, throttleTimeout)
      // add event listeners for window scroll and resize
      window.addEventListener('resize', reposition)
      window.addEventListener('scroll', reposition)
      // event listener to dismiss when a click is outside the child
      if (dismiss) window.addEventListener('click', dismiss)

      return function removeListeners() {
        // remove event listeners
        window.removeEventListener('resize', reposition)
        window.removeEventListener('scroll', reposition)
        if (dismiss) window.removeEventListener('click', dismiss)
      }
    },
    [throttleTimeout, dismiss, updatePosition],
  )

  React.useEffect(() => {
    const parentZ = getZindex(anchorRef.current)
    setZindex(parentZ + 1000)
  }, [anchorRef])

  React.useLayoutEffect(() => {
    // Calculate initial positioning of tether node.
    // We have to use a callback here because the actual dom node might not yet
    // be attached to the real DOM.
    // wait 10ms and show child, and use css transition to animate in
    if (stage === 'closing') {
      setTimeout(centerOnAnchor, 1)
    } else if (stage === 'opening') {
      setVisible(false)
      setTimeout(() => {
        centerOnAnchor()
        requestAnimationFrame(() => {
          updatePosition()
          setVisible(true)
        })
      }, 10)
    } else if (stage === 'open') {
      setTimeout(() => {
        updatePosition()
        requestAnimationFrame(() => {
          setVisible(true)
        })
      }, 1)
    }
  }, [updatePosition, setVisible, centerOnAnchor, stage])

  const transitionDuration = visible ? `${speed}ms` : '0ms'
  return createPortal(
    <div
      ref={childRef}
      onClick={stopBubble}
      className={cx('TetherPortal', stage, { visible }, className)}
      style={{
        zIndex: zIndex,
        transform: position.childTransform,
        transitionDuration,
      }}
    >
      <TetherArrow
        style={{
          transform: position.arrowTransform,
          transitionDuration,
          transitionDelay:
            visible && stage === 'opening' ? `${speed / 2}ms` : '0ms',
        }}
      />
      {children}
    </div>,
    portalContainer,
  )
}

const TetherWrapper = ({ open = false, speed = 200, dismiss, ...props }) => {
  // manage opening and closing state for mount and dismount transition
  // animations of the tether child
  const [stage, setStage] = React.useState(open ? 'open' : 'closed')
  React.useEffect(() => {
    // manage transition stages
    const [transitionStage, finalStage] = open
      ? ['opening', 'open']
      : ['closing', 'closed']

    // avoid re-render if the stage should not be updated by using the set
    // state callback form and returning currentStage when no update is needed
    // for example: don't set stage to "opening" when the current state is
    // already "open"
    setStage(currentStage =>
      currentStage === finalStage ? currentStage : transitionStage,
    )
    // transition to final stage after `speed` milliseconds
    const timeoutID = setTimeout(
      () =>
        setStage(currentStage =>
          currentStage === transitionStage ? finalStage : currentStage,
        ),
      speed,
    )
    return () => clearTimeout(timeoutID)
  }, [open, speed])
  return stage === 'closed' ? null : (
    <TetherPortal
      stage={stage}
      speed={speed}
      dismiss={open && dismiss}
      {...props}
    />
  )
}

export default TetherWrapper
